/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import FilterBar from './inner-components/FilterBar';
import FlightScheduleTable from './inner-components/FlightScheduleTable';
import CateringGroupTable from './inner-components/CateringGroupTable';
import './operation-monitor.scss';
import { DFPAPIEndpoint } from '../../utils/axios';
import { formatIsoTime } from '../../utils/commonFunctions';

// Status badge helper function
const mapItemStatus = item => {
  if (
    item.flightStatus === 'cancelled' ||
    item.scheduleStatus === 'cancelled'
  ) {
    return {
      status: 'CANCELLED',
      statusLabel: 'CANCELLED',
      disruptionNote:
        item.disruptionType ||
        item.iropInfo ||
        'ATC Flow Control Restrictions.',
    };
  }

  if (
    item.departureTimingStatus === 'significantlyDelayed' ||
    item.arrivalTimingStatus === 'significantlyDelayed' ||
    item.iropInfo
  ) {
    const depDelayStr = item.totalDepartureDelay?.formattedValue
      ? `Dep Delay: ${item.totalDepartureDelay.formattedValue}`
      : '';
    const arrDelayStr = item.totalArrivalDelay?.formattedValue
      ? `Arr Delay: ${item.totalArrivalDelay.formattedValue}`
      : '';
    const delayNote = [depDelayStr, arrDelayStr].filter(Boolean).join(' | ');
    return {
      status: 'IROP',
      statusLabel: 'IROP',
      disruptionNote:
        item.iropInfo || delayNote || 'Significant operational delay.',
    };
  }

  if (
    item.departureTimingStatus === 'delayed' ||
    item.arrivalTimingStatus === 'delayed'
  ) {
    const delayVal =
      item.totalDepartureDelay?.formattedValue ||
      item.totalArrivalDelay?.formattedValue;
    return {
      status: 'WARNING',
      statusLabel: 'DELAYED',
      disruptionNote: delayVal
        ? `Delay duration: ${delayVal}`
        : 'Operational delay reported.',
    };
  }

  return {
    status: 'ON TIME',
    statusLabel: 'ON TIME',
    disruptionNote: null,
  };
};

const parseApiFlightSchedules = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items.map(item => {
    const isCancelled =
      item.flightStatus === 'cancelled' || item.scheduleStatus === 'cancelled';
    const statusInfo = mapItemStatus(item);

    return {
      id: item.rotationId || `${item.flightNumber || ''}-${item.origin || ''}`,
      hubBase: item.origin || item.hubBase || '',
      tail: item.tailNumber || item.tail || '',
      sectorCode:
        item.sectorCode || (item.flightNumber ? `SK${item.flightNumber}` : ''),
      route:
        item.route ||
        (item.origin && item.destination
          ? `${item.origin} → ${item.destination}`
          : ''),
      std: item.std ? formatIsoTime(item.std) : item.stdTime || 'N/A',
      sta: item.sta ? formatIsoTime(item.sta) : item.staTime || 'N/A',
      atd: isCancelled
        ? 'N/A'
        : item.atd
          ? formatIsoTime(item.atd)
          : item.atdTime || 'N/A',
      ata: isCancelled
        ? 'N/A'
        : item.ata
          ? formatIsoTime(item.ata)
          : item.ataTime || 'N/A',
      actualDep: isCancelled
        ? 'N/A'
        : item.atd
          ? formatIsoTime(item.atd)
          : item.actualDep || 'N/A',
      isDelayed:
        item.isDelayed ??
        (item.departureTimingStatus === 'delayed' ||
          item.departureTimingStatus === 'significantlyDelayed'),
      isRecovery:
        item.isRecovery ??
        Boolean(
          item.disruptionType === 'recovery' ||
          (item.iropInfo && item.iropInfo.includes('recovery')),
        ),
      status: item.status || statusInfo.status,
      statusLabel: item.statusLabel || statusInfo.statusLabel,
      disruptionNote: item.disruptionNote ?? statusInfo.disruptionNote,
    };
  });
};

const parseApiCateringGroups = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items.map(item => {
    return {
      id: item.id,
      tailNumber: item.tailNumber,
      groupIdentity: item.groupIdentity || '',
      cateringGroupPath: item.cateringGroupPath || '',
      linkedSectorCount: item.linkedSectorCount || 0,
      groupStatus: item.groupStatus,
    };
  });
};

const parseBaselineOptions = (availableOptions = []) => {
  if (!Array.isArray(availableOptions)) return [];
  return availableOptions.map(opt => {
    if (typeof opt === 'string') return { value: opt, label: opt };
    return {
      value: opt.snapshotType,
      label: opt.label,
    };
  });
};

const defaultBaselineOptions = [{ value: 'baseline', label: 'Baseline' }];

function OperationMonitor() {
  const [activeTab, setActiveTab] = useState('flight'); // 'flight' or 'catering'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [baseline, setBaseline] = useState('baseline');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHub, setSelectedHub] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [flightSchedules, setFlightSchedules] = useState([]);
  const [cateringGroups, setCateringGroups] = useState([]);
  const [baselineOptions, setBaselineOptions] = useState(
    defaultBaselineOptions,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const dateFormatted = moment(selectedDate).format('YYYY-MM-DD');

    try {
      if (activeTab === 'flight') {
        let flightDataRes;

        try {
          flightDataRes = await DFPAPIEndpoint.get(
            `operations/flightPlans/sectors/${dateFormatted}?snapshotType=${baseline}`,
          );
        } catch (err) {
          console.warn(
            'DFPAPIEndpoint /flight-schedules failed, trying fallback API endpoint',
            err,
          );
        }

        if (Array.isArray(flightDataRes?.items)) {
          setFlightSchedules(parseApiFlightSchedules(flightDataRes?.items));
        } else {
          setFlightSchedules([]);
        }

        if (flightDataRes?.snapshot?.availableOptions) {
          setBaselineOptions(
            parseBaselineOptions(flightDataRes.snapshot.availableOptions),
          );
        }
      } else if (activeTab === 'catering') {
        let cateringDataRes;
        try {
          cateringDataRes = await DFPAPIEndpoint.get(
            `operations/flightPlans/catering-groups/${dateFormatted}?snapshotType=${baseline}`,
          );
        } catch (err) {
          console.warn('DFPAPIEndpoint /catering-groups failed', err);
        }

        if (Array.isArray(cateringDataRes?.cateringGroups)) {
          setCateringGroups(
            parseApiCateringGroups(cateringDataRes.cateringGroups),
          );
        } else {
          setCateringGroups([]);
        }

        if (cateringDataRes?.snapshot?.availableOptions) {
          setBaselineOptions(
            parseBaselineOptions(cateringDataRes.snapshot.availableOptions),
          );
        }
      }
    } catch (err) {
      console.error('Error fetching operation data:', err);
      setError(err?.message || 'Failed to fetch operation data');
      if (activeTab === 'flight') {
        setFlightSchedules([]);
      } else {
        setCateringGroups([]);
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedDate, baseline]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const hubOptions = Array.from(
    new Set(flightSchedules.map(item => item.hubBase).filter(Boolean)),
  ).map(value => ({ value, label: value }));

  const statusOptions = Array.from(
    new Set(flightSchedules.map(item => item.status).filter(Boolean)),
  ).map(value => ({ value, label: value }));

  const filteredFlights = flightSchedules.filter(item => {
    const term = searchTerm.toLowerCase().trim();
    const matchesSearch =
      !term ||
      (item.hubBase && item.hubBase.toLowerCase().includes(term)) ||
      (item.tail && item.tail.toLowerCase().includes(term)) ||
      (item.sectorCode && item.sectorCode.toLowerCase().includes(term)) ||
      (item.route && item.route.toLowerCase().includes(term)) ||
      (item.status && item.status.toLowerCase().includes(term));
    const matchesHub = !selectedHub || item.hubBase === selectedHub;
    const matchesStatus = !selectedStatus || item.status === selectedStatus;

    return matchesSearch && matchesHub && matchesStatus;
  });

  const cateringHubOptions = [];

  const cateringStatusOptions = Array.from(
    new Set(cateringGroups.map(item => item.groupStatus).filter(Boolean)),
  ).map(value => ({ value, label: value }));

  const filteredCatering = cateringGroups.filter(item => {
    const term = searchTerm.toLowerCase().trim();
    const matchesSearch =
      !term ||
      (item.groupIdentity && item.groupIdentity.toLowerCase().includes(term)) ||
      (item.tailNumber && item.tailNumber.toLowerCase().includes(term)) ||
      (item.cateringGroupPath && item.cateringGroupPath.toLowerCase().includes(term)) ||
      (item.groupStatus && item.groupStatus.toLowerCase().includes(term));

    const matchesStatus = !selectedStatus || item.groupStatus === selectedStatus;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="op-monitor-page">
      {/* 1. Filter Bar with Date Picker, Baseline Selector, and Search Input */}
      <FilterBar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        baseline={baseline}
        onBaselineChange={setBaseline}
        baselineOptions={baselineOptions}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        hubOptions={activeTab === 'flight' ? hubOptions : cateringHubOptions}
        selectedHub={selectedHub}
        onHubChange={setSelectedHub}
        statusOptions={
          activeTab === 'flight' ? statusOptions : cateringStatusOptions
        }
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
      />

      {/* 2. Table Section Container */}
      <section className="monitor-card table-card">
        {activeTab === 'flight' ? (
          <FlightScheduleTable flights={filteredFlights} loading={loading} />
        ) : (
          <CateringGroupTable
            cateringGroups={filteredCatering}
            loading={loading}
          />
        )}
      </section>
    </div>
)
};

export default OperationMonitor;
